import { Button, Checkbox, CircularProgress } from "@mui/material";
import { MuiBox, MuiTypography } from "components/atoms";
import CheckboxChecked from "assets/images/svg-icons/DarkBlueCheckboxChecked.svg";
import CheckboxUnChecked from "assets/images/svg-icons/checkboxUnchecked.svg";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { AcceptTermsAction } from "store/middlewares/session/auth";
import LatestTermsModalSkeleton from "../SkeletonLoaders/LatestTermsModalSkeleton";
import { GetStaticPagesAction } from "store/middlewares/general";

const LatestTermsModal = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { staticPages, staticPagesLoading } = useSelector(
    (state) => state.general,
  );
  const { acceptTermsLoading } = useSelector((state) => state.auth);
  const [hasConfirmedTerms, setHasConfirmedTerms] = useState(false);

  useEffect(() => {
    dispatch(GetStaticPagesAction({ type: "terms" }));
  }, [dispatch]);

  const handleAccept = async () => {
    if (!hasConfirmedTerms || acceptTermsLoading) return;

    dispatch(AcceptTermsAction());
  };

  return (
    <MuiBox sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
      <MuiBox
        sx={{
          bgcolor: "background.white",
          p: "16px 20px",
          borderRadius: "12px 12px 0 0",
        }}
      >
        <MuiTypography variant="20" fontWeight="600" color="text.darkBlack">
          {t("modal.latestTermsModal.termsAndConditions")}
        </MuiTypography>
      </MuiBox>

      <MuiBox
        className="static-pages no-scrollbar"
        sx={{
          bgcolor: "background.white",
          height: "min(60vh, 620px)",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {staticPagesLoading ? (
          <LatestTermsModalSkeleton />
        ) : (
          <>
            <MuiBox
              sx={{
                p: "20px",
                display: "flex",
                flex: 1,
                flexDirection: "column",
                gap: "12px",
              }}
            >
             <MuiTypography
                variant="22"
                fontWeight="600"
                color="text.darkBlack"
              >
                {staticPages?.list?.[0]?.title}
              </MuiTypography>
              {staticPages?.last_modified && (
                <MuiTypography
                  variant="14"
                  fontWeight="500"
                  color="text.lightgray"
                  sx={{ display: "block", mb: "20px" }}
                >
                  {t("modal.latestTermsModal.lastModified", {
                    date: dayjs(staticPages.last_modified).format(
                      "MMMM D, YYYY",
                    ),
                  })}
                </MuiTypography>
              )}
              {staticPages?.list?.[0] && (
                <MuiBox
                  sx={{
                    color: "text.darkBlack",
                    "& *": { color: "inherit !important" },
                  }}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: staticPages.list[0].content,
                    }}
                  />
                </MuiBox>
              )}
              <MuiBox
                component="label"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: "24px",
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  icon={
                    <img
                      src={CheckboxUnChecked}
                      alt=""
                      width={16}
                      height={16}
                    />
                  }
                  checkedIcon={
                    <img src={CheckboxChecked} alt="" width={16} height={16} />
                  }
                  disableRipple
                  checked={hasConfirmedTerms}
                  onChange={(event) =>
                    setHasConfirmedTerms(event.target.checked)
                  }
                  sx={{ p: 0, mr: "8px" }}
                  inputProps={{
                    "aria-label": t(
                      "modal.latestTermsModal.termsAndConditionsConfirmationMessage",
                    ),
                  }}
                />
                <MuiTypography
                  variant="14"
                  fontWeight="500"
                  color="text.darkBlack"
                >
                  {t(
                    "modal.latestTermsModal.termsAndConditionsConfirmationMessage",
                  )}
                </MuiTypography>
              </MuiBox>
            </MuiBox>

            <MuiBox
              sx={{
                bgcolor: "background.gray200",
                p: "16px 20px",
                borderRadius: "0 0 12px 12px",
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                id="center-modal-primary-action"
                variant="contained"
                sx={{ width: "fit-content" }}
                disabled={!hasConfirmedTerms || acceptTermsLoading}
                onClick={handleAccept}
              >
                {acceptTermsLoading ? (
                  <CircularProgress size={20} thickness={5} color="inherit" />
                ) : (
                  <MuiTypography variant="14" fontWeight="500" component="span">
                    {t("modal.latestTermsModal.accept")}
                  </MuiTypography>
                )}
              </Button>
            </MuiBox>
          </>
        )}
      </MuiBox>
    </MuiBox>
  );
};

export default LatestTermsModal;
